import React, { useEffect, ReactNode, useState } from "react";
import {
  PaginationSortingDirection,
  RoomFilterField,
  RoomsListEdgesFragment,
  RoomSortField,
  RoomSortObject,
  SiteAndRoomComparisonOperator,
  useRoomsListForTenantQuery,
  useSitesCountForTenantQuery
} from "generated/graphql";
import {
  ToolTip,
  PolyIcon,
  NoResultsIndicatorAnimation,
  NoResultsIndicator
} from "elements";
import { useTranslation } from "utilities/i18n";
import { handleNullFields, nameIsUnassigned } from "utilities/stringUtils";
import { infiniteNestLink, Link } from "polyrouter";
import {
  TableBody,
  TableBulkItemRow,
  TableBulkItemHeader,
  toggleItemWithSet,
  itemCheckedWithSet,
  toggleAll,
  HEADER_ID,
  TableHeaderItem,
  TableSearchEdit,
  getColumns,
  TableVisibilitySensor
} from "collections/Table";
import CreateRoomModal from "routes/Manage/Rooms/Create/CreateRoomModal";
import RemoveRoomsModal from "routes/Manage/Rooms/Delete/DeteleRoomModal";
import { ProtectedButton, ProtectedLink, ProtectedRoute } from "TsRoleGuards";
import { needs, useGuard } from "rbac";
import { SortDirection, SortState } from "store/filters";
import { TABLES, TABLE_COLUMN_STATE } from "store/userMeta";
import { useSelector } from "react-redux";
import { State, useTypesafeActions } from "store";
import { generateTableHeaders } from "utilities/tableUtils";
import { Layout } from "views/Main/Layouts";
import { ActionType, createStandardAction, getType } from "typesafe-actions";

export type RoomDetails = RoomsListEdgesFragment & {
  deviceCount?: number;
  percentOnline?: number;
  siteName?: string;
};

type RoomRowProps = {
  room: RoomsListEdgesFragment["node"];
  onCheck: (id: string) => void;
  checked: boolean;
  showChecks: boolean;
  shownColumns: Array<string>;
};

export const RoomRow: React.FC<RoomRowProps> = ({
  room,
  onCheck,
  checked,
  showChecks,
  shownColumns
}) => {
  const {
    id,
    name,
    deviceCount,
    devicesOnlinePercentage,
    site,
    capacity
  } = room;
  const siteName = site.name;

  const { COLUMNS } = TABLES.ROOMS;

  const getTableContent = (column: string) => {
    let content: ReactNode = null;
    switch (column) {
      case COLUMNS.NAME:
        content = (
          <Link nest to={`/details/${id}`}>
            {handleNullFields(name)}
          </Link>
        );
        break;
      case COLUMNS.SITE:
        content = siteName;
        break;
      case COLUMNS.DEVICES:
        content = deviceCount;
        break;
      case COLUMNS.CAPACITY:
        content = capacity ?? "-";
        break;
      case COLUMNS.PERCENT_ONLINE:
        content = devicesOnlinePercentage;
        break;
    }

    return <td key={column}>{content}</td>;
  };

  return (
    <TableBulkItemRow
      checked={checked}
      onCheck={() => onCheck(id)}
      showChecks={showChecks}
      replaceCheckWith={
        nameIsUnassigned(name) && <PolyIcon icon="warning_triangle" />
      }
    >
      {shownColumns.map(getTableContent)}
    </TableBulkItemRow>
  );
};

export const AddRoomButton: React.FC<{
  disabled: boolean;
  tooltipPosition?: "top" | "right" | "bottom" | "left";
}> = ({ disabled, tooltipPosition = "bottom" }) => {
  const { t } = useTranslation();
  return (
    <>
      {disabled ? (
        <ToolTip
          iconTitle={t("ADD_NEW")}
          position={tooltipPosition}
          iconReplacement={
            <ProtectedButton
              disabled
              className="btn btn-primary mr-1 disabled"
              data-testid="add-room"
              requirement="UPDATE_SITES"
            >
              {t("ADD_NEW")}
            </ProtectedButton>
          }
        >
          <p>
            {t("CREATE_SITE_FIRST_1")}
            <ProtectedLink to="/manage/sites" requirement="UPDATE_SITES">
              {t("SITE_MANAGER")}
            </ProtectedLink>
            {t("CREATE_SITE_FIRST_2")}
          </p>
        </ToolTip>
      ) : (
        <ProtectedLink
          to={infiniteNestLink("/add")}
          className="btn btn-primary"
          data-testid="add-room"
          requirement="UPDATE_SITES"
        >
          {t("ADD_NEW")}
        </ProtectedLink>
      )}
    </>
  );
};

type RoomsListState = {
  selectedItems: string[];
  sort: SortState;
  searchTerm?: string;
  cursor: string;
  totalRoomCount: number;
  filteredRoomCount: number;
  rooms: RoomDetails[];
  hasNextPage: boolean;
  endCursor: string;
};

type PageInfo = Pick<
  RoomsListState,
  "totalRoomCount" | "filteredRoomCount" | "rooms" | "hasNextPage" | "endCursor"
>;

const Actions = {
  updateSelectedItems: createStandardAction("UpdateSelectedItems")<string[]>(),
  updateSort: createStandardAction("UpdateSort")<SortState>(),
  updateSearch: createStandardAction("UpdateSearch")<string | undefined>(),
  updateCursor: createStandardAction("UpdateCursor")<string>(),
  updatePageInfo: createStandardAction("UpdatePageInfo")<PageInfo>()
};

type ActionTypes = typeof Actions;

function reducer(
  state: RoomsListState,
  a: ActionType<ActionTypes>
): RoomsListState {
  switch (a.type) {
    case getType(Actions.updateSelectedItems):
      return {
        ...state,
        selectedItems: a.payload
      };
    case getType(Actions.updateSort):
      return {
        ...state,
        cursor: "",
        sort: a.payload
      };
    case getType(Actions.updateSearch):
      const term = a.payload;
      return {
        ...state,
        cursor: term !== undefined ? "" : state.cursor,
        searchTerm: term
      };
    case getType(Actions.updateCursor):
      return {
        ...state,
        cursor: a.payload
      };
    case getType(Actions.updatePageInfo):
      return {
        ...state,
        ...a.payload
      };
  }
}

const getSort = (
  column: string,
  direction: SortDirection
): RoomSortObject[] => {
  const dir =
    direction === "ASC"
      ? PaginationSortingDirection.Asc
      : PaginationSortingDirection.Desc;
  const mapColumnToSortFields = {
    [TABLES.ROOMS.COLUMNS.NAME]: [
      {
        field: RoomSortField.UnassignedRoomNameFirst,
        direction: PaginationSortingDirection.Desc
      },
      {
        field: RoomSortField.RoomName,
        direction: dir
      }
    ],
    [TABLES.ROOMS.COLUMNS.CAPACITY]: [
      {
        field: RoomSortField.RoomCapacity,
        direction: dir
      }
    ],
    [TABLES.ROOMS.COLUMNS.SITE]: [
      {
        field: RoomSortField.UnassignedSiteNameFirst,
        direction: PaginationSortingDirection.Desc
      },
      {
        field: RoomSortField.SiteName,
        direction: dir
      }
    ]
  };

  return mapColumnToSortFields[column];
};

export const RoomsList: React.FC<{
  tenantId: string;
}> = ({ tenantId }) => {
  const { Content } = Layout;
  const { t } = useTranslation();
  const initialState: RoomsListState = {
    selectedItems: [],
    sort: {
      field: "name",
      direction: "ASC"
    },
    searchTerm: "",
    cursor: "",
    totalRoomCount: 0,
    filteredRoomCount: 0,
    rooms: [],
    hasNextPage: false,
    endCursor: ""
  };

  const [
    {
      selectedItems,
      sort,
      searchTerm,
      cursor,
      totalRoomCount,
      filteredRoomCount,
      rooms,
      hasNextPage,
      endCursor
    },
    {
      updateCursor,
      updateSearch,
      updateSelectedItems,
      updateSort,
      updatePageInfo
    }
  ] = useTypesafeActions<RoomsListState, ActionTypes>(
    reducer,
    initialState,
    Actions
  );
  const [
    sitesWithoutUnassignedCount,
    setSitesWithoutUnassignedCount
  ] = useState(0);

  const userCanEdit = useGuard(needs("UPDATE_SITES"));

  const { NAME: TABLE_NAME, COLUMNS } = TABLES.ROOMS;
  const {
    [TABLE_COLUMN_STATE.SHOWN]: shownColumns,
    [TABLE_COLUMN_STATE.HIDDEN]: hiddenColumns
  } = useSelector((state: State) => {
    return state.userMeta?.tables[TABLE_NAME] ?? {};
  });

  useEffect(() => {
    updateSelectedItems([]);
  }, [tenantId, updateSelectedItems]);

  const filterParam = searchTerm
    ? [
        {
          field: RoomFilterField.Name,
          comparisonOperator:
            SiteAndRoomComparisonOperator.CaseInsensitiveContains,
          value: searchTerm
        }
      ]
    : undefined;

  const params = {
    cursor,
    limit: 100,
    filter: filterParam,
    sort: getSort(sort.field, sort.direction)
  };

  const {
    data: roomsData,
    fetching: roomsFetching,
    stale: roomsStale,
    executor
  } = useRoomsListForTenantQuery({
    variables: {
      tenantId,
      params
    },
    requestPolicy: "network-only"
  });

  const {
    data: siteCountData,
    fetching: siteCountFetching,
    stale: siteCountStale
  } = useSitesCountForTenantQuery({
    variables: {
      tenantId
    }
  });

  useEffect(() => {
    if (
      roomsData?.tenant.roomData &&
      roomsData?.tenant.roomCount &&
      !roomsStale &&
      !roomsFetching
    ) {
      const {
        total: filteredRoomCount,
        edges: rooms,
        pageInfo
      } = roomsData.tenant.roomData;
      const { total: totalRoomCount } = roomsData.tenant.roomCount;

      updatePageInfo({
        totalRoomCount,
        filteredRoomCount,
        rooms: rooms ?? [],
        hasNextPage: pageInfo.hasNextPage,
        endCursor: pageInfo.endCursor
      });
    }
  }, [roomsData, updatePageInfo, roomsStale, roomsFetching]);

  useEffect(() => {
    if (
      siteCountData?.tenant.siteCount &&
      !siteCountStale &&
      !siteCountFetching
    ) {
      const totalSiteCount = siteCountData.tenant.siteCount.total;
      const hasUnassigned = !!siteCountData.tenant.siteCount.edges?.some(
        ({ node: site }) => nameIsUnassigned(site.name)
      );
      setSitesWithoutUnassignedCount(
        hasUnassigned ? totalSiteCount - 1 : totalSiteCount
      );
    }
  }, [siteCountData, siteCountFetching, siteCountStale]);

  const toggleItem = toggleItemWithSet(
    rooms.length,
    selectedItems,
    updateSelectedItems
  );
  const itemChecked = itemCheckedWithSet(selectedItems);

  const roomsToDelete = selectedItems.reduce((roomsList, id) => {
    const roomObj = rooms.find(room => room.node.id === id);
    return roomObj
      ? [...roomsList, { name: roomObj.node.name, id }]
      : roomsList;
  }, [] as Array<{ name: string; id: string }>);

  const refresh = () => {
    updateSelectedItems([]);
    executor({
      requestPolicy: "network-only"
    });
  };

  const columnTranslations = {
    [COLUMNS.NAME]: t("ROOM"),
    [COLUMNS.SITE]: t("SITE"),
    [COLUMNS.DEVICES]: t("DEVICES"),
    [COLUMNS.PERCENT_ONLINE]: t("PERCENT_ONLINE"),
    [COLUMNS.CAPACITY]: t("CAPACITY")
  };

  const getTableHeaders = (column: string, title: string) => {
    const sortable = !!getSort(column, sort.direction);
    const sortObj = sortable
      ? {
          currentSort: sort,
          onSort: updateSort
        }
      : undefined;

    return (
      <TableHeaderItem
        fieldName={column}
        title={title}
        sort={sortObj}
        key={column}
      />
    );
  };

  const shown = getColumns(shownColumns, columnTranslations);
  const hidden = getColumns(hiddenColumns, columnTranslations);

  const onChange = (isVisible: boolean) => {
    if (isVisible && cursor !== endCursor) {
      updateCursor(endCursor);
    }
  };

  const loading =
    roomsFetching || siteCountFetching || roomsStale || siteCountStale;
  const tableLoading = loading && !cursor;
  return (
    <Layout>
      <Content className="no-sidebar">
        {!loading && !totalRoomCount ? (
          <NoResultsIndicatorAnimation title={t("NO_ROOMS")}>
            <AddRoomButton
              disabled={sitesWithoutUnassignedCount === 0}
              tooltipPosition="top"
            />
          </NoResultsIndicatorAnimation>
        ) : (
          <>
            <div className="table-heading">
              <span className="metric" data-testid="room-count">
                {filteredRoomCount}
              </span>
              <span className="title ml-5">{t("ROOMS")}</span>
            </div>
            <div className="grouping mb-15 table-actions">
              <AddRoomButton disabled={sitesWithoutUnassignedCount === 0} />
              {selectedItems.length > 0 ? (
                <ProtectedLink
                  to={infiniteNestLink("/remove")}
                  className="btn"
                  data-testid="remove-room"
                  requirement="UPDATE_SITES"
                >
                  {t("REMOVE_ROOM", {
                    count: selectedItems.filter(item => item !== HEADER_ID)
                      .length
                  })}
                </ProtectedLink>
              ) : (
                <ProtectedLink
                  nest
                  to={""}
                  className="btn disabled"
                  data-testid="remove-room"
                  requirement="UPDATE_SITES"
                >
                  {t("REMOVE_ROOM")}
                </ProtectedLink>
              )}
              <TableSearchEdit
                search={{
                  onSearch: updateSearch,
                  searchPlaceholder: t("SEARCH_ROOM")
                }}
                edit={{
                  tableName: TABLE_NAME,
                  shownColumns: shown,
                  hiddenColumns: hidden
                }}
              />
            </div>
            <div className="table-container">
              <table className="table custom-table">
                <TableBulkItemHeader
                  checked={itemChecked(HEADER_ID)}
                  onCheck={() =>
                    toggleAll(
                      rooms
                        .filter(room => !nameIsUnassigned(room.node.name))
                        .map(room => room.node.id),
                      selectedItems,
                      updateSelectedItems
                    )
                  }
                  showChecks={userCanEdit && !!shownColumns.length}
                >
                  {generateTableHeaders(shown, getTableHeaders)}
                </TableBulkItemHeader>
                <TableBody
                  loading={tableLoading}
                  cols={shown.length}
                  accommodateScrollbar
                >
                  {rooms.map(room => (
                    <RoomRow
                      room={room.node}
                      key={room.node.id}
                      onCheck={() => toggleItem(room.node.id)}
                      checked={itemChecked(room.node.id)}
                      showChecks={userCanEdit && !!shownColumns.length}
                      shownColumns={shown.map(column => column.key)}
                    />
                  ))}
                  {hasNextPage && <TableVisibilitySensor onChange={onChange} />}
                  {!loading && !rooms.length ? (
                    <tr>
                      <td>
                        <NoResultsIndicator
                          className="inline mt-5"
                          title={t("NO_ROOMS")}
                        />
                      </td>
                    </tr>
                  ) : null}
                </TableBody>
              </table>
            </div>
          </>
        )}
        <ProtectedRoute nest path="/add" requirement="UPDATE_SITES">
          <CreateRoomModal refresh={refresh} withSites={false} />
        </ProtectedRoute>
        <ProtectedRoute nest path="/remove" requirement="UPDATE_SITES">
          <RemoveRoomsModal refresh={refresh} rooms={roomsToDelete} />
        </ProtectedRoute>
      </Content>
    </Layout>
  );
};
